import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Brand, SidebarNav } from "@/components/dashboard/sidebar";
import { NotificationsBell } from "@/components/dashboard/notifications-bell";
import { LanguageSwitcher } from "@/components/language-switcher";
import { ThemeToggle } from "@/components/theme-toggle";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
} from "lucide-react";
import { cn, initials } from "@/lib/utils";
import { useDict } from "@/lib/i18n/context";
import type { User } from "@/lib/types";

const COLLAPSE_KEY = "hiresensor-sidebar-collapsed";

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

export function DashboardLayout({
  user,
  onSignOut,
  children,
}: {
  user: User;
  onSignOut: () => void;
  children?: React.ReactNode;
}) {
  const t = useDict();
  const location = useLocation();
  const name = user.full_name || user.email || "User";
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  const signOut = async () => {
    await fetch("/api/auth/sign-out", {
      method: "POST",
      credentials: "include",
    });
    onSignOut();
    window.location.href = "/sign-in";
  };

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const renderSidebar = (opts: {
    collapsed: boolean;
    onNavigate?: () => void;
    endAction?: React.ReactNode;
    showCollapseToggle?: boolean;
  }) => (
    <>
      <div className="space-y-6">
        <div
          className={cn(
            "flex items-center gap-2",
            opts.collapsed ? "flex-col" : "justify-between",
          )}
        >
          <Brand collapsed={opts.collapsed} onNavigate={opts.onNavigate} />
          {opts.endAction}
          {opts.showCollapseToggle && !opts.collapsed && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hidden h-8 w-8 shrink-0 p-0 md:inline-flex"
              onClick={toggleCollapsed}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </Button>
          )}
        </div>
        {opts.showCollapseToggle && opts.collapsed && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mx-auto hidden h-8 w-8 p-0 md:inline-flex"
            onClick={toggleCollapsed}
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4 rtl:rotate-180" />
          </Button>
        )}
        <SidebarNav
          userEmail={user.email}
          collapsed={opts.collapsed}
          onNavigate={opts.onNavigate}
        />
      </div>
      <Button
        variant="ghost"
        className={cn(
          "w-full",
          opts.collapsed ? "justify-center px-2" : "justify-start",
        )}
        type="button"
        onClick={signOut}
        title={t.common.signOut}
      >
        <LogOut className="h-5 w-5 shrink-0" />
        {!opts.collapsed && <span>{t.common.signOut}</span>}
      </Button>
    </>
  );

  return (
    <div className="flex min-h-screen min-w-0">
      {/* Desktop in-flow sidebar */}
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col justify-between border-e border-border bg-card transition-[width] duration-200 md:flex",
          collapsed ? "w-[4.5rem] p-2 py-4" : "w-64 p-4",
        )}
      >
        {renderSidebar({
          collapsed,
          showCollapseToggle: true,
        })}
      </aside>

      {/* Mobile drawer backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        className={cn(
          "fixed inset-0 z-40 bg-black/40 transition-opacity md:hidden",
          mobileOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0",
        )}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile overlay drawer — start edge (LTR left / RTL right) */}
      <aside
        className={cn(
          "fixed inset-y-0 start-0 z-50 flex w-72 max-w-[85vw] flex-col justify-between border-e border-border bg-card p-4 shadow-xl transition-transform duration-200 md:hidden",
          mobileOpen
            ? "translate-x-0"
            : "-translate-x-full rtl:translate-x-full",
        )}
      >
        {renderSidebar({
          collapsed: false,
          onNavigate: () => setMobileOpen(false),
          endAction: (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </Button>
          ),
        })}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex min-w-0 items-center justify-between gap-3 border-b border-border bg-card/80 px-3 py-3 backdrop-blur sm:gap-4 sm:px-4 md:px-8">
          <div className="flex min-w-0 items-center gap-2 md:hidden">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9 w-9 shrink-0 p-0"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Brand />
          </div>
          <div className="ms-auto flex shrink-0 items-center gap-2.5 sm:gap-3">
            <ThemeToggle />
            <LanguageSwitcher />
            <NotificationsBell />
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700 dark:bg-brand-950 dark:text-brand-200">
                {initials(name)}
              </span>
              <span className="hidden max-w-[10rem] truncate text-sm font-medium lg:inline">
                {name}
              </span>
            </div>
          </div>
        </header>

        <main className="min-w-0 flex-1 p-3 sm:p-4 md:p-8">
          {children ?? <Outlet />}
        </main>
      </div>
    </div>
  );
}
